import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, Building2, Hospital, TrainFront, Landmark, LayoutGrid, TrendingUp, Wrench } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import MapView from '../components/common/MapView';
import InvestorCalculators from '../components/investor/InvestorCalculators';

const TYPE_ICON = {
    transit: TrainFront,
    hospital: Hospital,
    community: Landmark,
    mixed_use: Building2,
    infrastructure: Wrench,
};

export default function InvestorCentre() {
    const { data } = useQuery({
        queryKey: ['investor-centre'],
        queryFn: async () => (await api.get('/investor-centre')).data,
    });

    return (
        <div className="bg-white">
            <SEOHead
                title="Investor Centre — Southern Ontario"
                description="Long-term investor education, calculators, and Southern Ontario development, rental, builder and infrastructure intelligence. Yield-first, cycle-tested."
                path="/investor-centre"
            />

            {/* ==================== COVER ==================== */}
            <section className="border-b border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pb-28">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        The Investor Centre · Southern Ontario
                    </div>
                    <h1
                        className="font-editorial mt-8 leading-[1.02] text-black max-w-4xl"
                        style={{ fontSize: 'clamp(52px, 8vw, 108px)', letterSpacing: '-0.03em' }}
                        data-testid="investor-centre-title"
                    >
                        Yield first.
                        <br />
                        <span className="italic text-neutral-500">Speculation last.</span>
                    </h1>
                    <p className="mt-8 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: 'clamp(17px, 1.3vw, 21px)', lineHeight: 1.55 }}>
                        Southern Ontario&apos;s investor intelligence hub for buy-and-hold operators.
                        Calculators, rankings, development maps, rental data, builder projects,
                        infrastructure programme timelines. Written for people who plan to own
                        the same building in ten years.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <a
                            href="#calculators"
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-7 py-4 text-[14px] font-medium hover:bg-neutral-800"
                            style={{ letterSpacing: '0.02em' }}
                            data-testid="investor-cta-calculators"
                        >
                            Run the calculators
                        </a>
                        <Link
                            to="/investors/strategy-session"
                            className="inline-flex items-center gap-2 border border-neutral-400 text-black px-7 py-4 text-[14px] font-medium hover:bg-brand-navy hover:text-white transition-colors"
                           
                            data-testid="investor-cta-session"
                        >
                            Book a strategy session
                        </Link>
                    </div>
                </div>
            </section>

            {/* ==================== INVESTOR PRINCIPLES ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Investor principles</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        Six positions we hold on every file.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-neutral-200">
                        {(data?.principles || []).map((p, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
                                className="border-r border-b border-neutral-200 p-6 lg:p-8"
                            >
                                <div className="font-mono-data text-[10.5px] tracking-widest text-neutral-500">
                                    P{String(i + 1).padStart(2, '0')}
                                </div>
                                <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '22px', letterSpacing: '-0.01em' }}>
                                    {p.kicker}
                                </div>
                                <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-700">
                                    {p.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== CALCULATORS ==================== */}
            <section id="calculators" className="border-b border-neutral-200 bg-[#FAFAF8] scroll-mt-16">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Calculators</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        The three that matter.
                    </h2>
                    <p className="mt-4 text-neutral-600 max-w-2xl">
                        Cash flow. Return on invested capital. Cap rate. Every serious acquisition
                        gets defended in these three views before an offer goes in.
                    </p>
                    <div className="mt-10">
                        <InvestorCalculators />
                    </div>

                    {/* Prominent link to the advanced projection calculator */}
                    <Link
                        to="/investor-centre/investment-projection-calculator"
                        data-testid="investor-cta-projection"
                        className="mt-6 block border border-brand-navy bg-brand-navy text-white p-6 lg:p-7 hover:bg-brand-gold hover:text-brand-navy transition-colors group"
                       
                    >
                        <div className="flex items-center gap-2 text-[10.5px] font-mono-data tracking-widest uppercase text-white/60 group-hover:text-neutral-500">
                            New · Full projection engine
                        </div>
                        <div className="mt-3 flex items-start justify-between gap-4">
                            <div>
                                <div className="font-editorial group-hover:italic" style={{ fontSize: 26, letterSpacing: '-0.01em' }}>
                                    Advanced Real Estate Investment Projection Calculator
                                </div>
                                <p className="mt-2 text-[14px] text-white/85 group-hover:text-neutral-700 max-w-2xl">
                                    Model the full 1-25 year financial performance — income, expenses,
                                    mortgage amortisation, appreciation, equity growth and total return.
                                    Each return component displayed separately.
                                </p>
                            </div>
                            <ArrowUpRight className="w-5 h-5 shrink-0" />
                        </div>
                    </Link>
                </div>
            </section>

            {/* ==================== RANKINGS ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="overline">Neighbourhood rankings</div>
                        <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                            Southern Ontario, ranked by five-year forecast.
                        </h2>
                        <p className="mt-6 text-neutral-700 max-w-xl">
                            We keep the rankings on a dedicated dark terminal — sortable, dense,
                            comparable. Our model weighs transit velocity, zoning changes, amenity
                            density, and the trend momentum from the last five transaction years.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <Link
                            to="/invest"
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-7 py-4 text-[14px] font-medium hover:bg-neutral-800"
                            style={{ letterSpacing: '0.02em' }}
                            data-testid="investor-cta-rankings"
                        >
                            Open the investment terminal <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ==================== FUTURE DEVELOPMENT MAP ==================== */}
            <section className="border-b border-neutral-200 bg-[#F7F7F5]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Future development map</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        {(data?.developments || []).length} public capital projects on the map.
                    </h2>
                    <p className="mt-4 text-neutral-600 max-w-2xl">
                        Transit corridors, hospital modernisations, university expansions and
                        mixed-use nodes shape long-term residential demand. Every marker below is a
                        five-to-fifteen year positioning signal.
                    </p>
                    <div className="mt-10">
                        <MapView
                            items={(data?.developments || []).map((d) => ({
                                slug: d.slug,
                                name: d.name,
                                city: d.region,
                                province: 'ON',
                                coords: d.coords,
                                median_price: 0,
                                forecast_5yr_pct: 0,
                            }))}
                            height={520}
                            zoom={9}
                        />
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-neutral-200" data-testid="dev-list">
                        {(data?.developments || []).map((d) => {
                            const Icon = TYPE_ICON[d.type] || LayoutGrid;
                            return (
                                <div key={d.slug} className="border-r border-b border-neutral-200 p-6 bg-white">
                                    <div className="flex items-center gap-2 text-[10.5px] tracking-widest uppercase text-neutral-500 font-mono-data">
                                        <Icon className="w-3.5 h-3.5" />
                                        {d.type.replace('_', ' ')} · {d.horizon_years}
                                    </div>
                                    <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '18px', letterSpacing: '-0.01em' }}>
                                        {d.name}
                                    </div>
                                    <div className="text-[11.5px] text-neutral-500 mt-1 font-mono-data">
                                        {d.region} · Confidence: {d.confidence}
                                    </div>
                                    <p className="mt-3 text-[13.5px] leading-relaxed text-neutral-700">
                                        {d.impact}
                                    </p>
                                    <div className="mt-3 text-[10.5px] text-neutral-400 font-mono-data">
                                        Source: {d.source}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ==================== RENTAL MARKET ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Rental market</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        Where the yield actually is.
                    </h2>
                    <div className="mt-10 border border-neutral-200 overflow-x-auto" data-testid="rental-table">
                        <table className="w-full text-[13.5px]">
                            <thead className="bg-[#FAFAF8] border-b border-neutral-200 text-[10.5px] font-mono-data tracking-widest uppercase text-neutral-500">
                                <tr>
                                    <th className="text-left p-4">Region</th>
                                    <th className="text-right p-4">Median 2BR</th>
                                    <th className="text-right p-4">YoY</th>
                                    <th className="text-right p-4">Vacancy</th>
                                    <th className="text-right p-4">Est. gross yield</th>
                                    <th className="text-left p-4 hidden md:table-cell">Take</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.rental_market || []).map((r) => (
                                    <tr key={r.region} className="border-b border-neutral-100 last:border-none hover:bg-[#FAFAF8]">
                                        <td className="p-4">
                                            <Link to={`/${r.region_slug}`} className="font-editorial text-[16px] tracking-tight hover:italic">
                                                {r.region}
                                            </Link>
                                        </td>
                                        <td className="text-right p-4 font-mono-data tabular">${r.median_rent_2br.toLocaleString()}</td>
                                        <td className="text-right p-4 font-mono-data tabular text-[#2F6B47]">+{r.yoy_change_pct.toFixed(1)}%</td>
                                        <td className="text-right p-4 font-mono-data tabular">{r.vacancy_pct.toFixed(1)}%</td>
                                        <td className="text-right p-4 font-mono-data tabular font-medium">{r.gross_yield_est_pct.toFixed(1)}%</td>
                                        <td className="p-4 text-neutral-700 hidden md:table-cell">{r.insight}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ==================== BUILDER PROJECTS ==================== */}
            <section className="border-b border-neutral-200 bg-[#F7F7F5]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Builder projects</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        The active builds worth watching.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-neutral-200" data-testid="builder-list">
                        {(data?.builders || []).map((b) => (
                            <div key={b.slug} className="border-r border-b border-neutral-200 p-6 lg:p-8 bg-white">
                                <div className="text-[10.5px] tracking-widest uppercase text-neutral-500 font-mono-data">
                                    {b.region} · {b.delivery_years}
                                </div>
                                <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '20px', letterSpacing: '-0.01em' }}>
                                    {b.name}
                                </div>
                                <div className="text-[12.5px] text-neutral-500 mt-1">
                                    {b.builder} · {b.type}
                                </div>
                                <p className="mt-4 text-[14px] leading-relaxed text-neutral-800">
                                    <span className="font-medium text-black">Investor lens · </span>
                                    {b.investor_lens}
                                </p>
                                <p className="mt-2 text-[13px] leading-relaxed text-neutral-500 italic">
                                    Cycle risk: {b.cycle_risk}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== INFRASTRUCTURE ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Infrastructure</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        Five programmes reshaping the region.
                    </h2>
                    <div className="mt-10 border-t border-neutral-200" data-testid="infra-list">
                        {(data?.infrastructure || []).map((it, i) => (
                            <div key={i} className="border-b border-neutral-200 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-1 font-mono-data text-[13px] tabular text-neutral-400">
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <div className="lg:col-span-4">
                                    <div className="font-editorial leading-tight" style={{ fontSize: '20px', letterSpacing: '-0.01em' }}>
                                        {it.name}
                                    </div>
                                    <div className="mt-2 text-[11px] font-mono-data tracking-wider uppercase text-neutral-500">
                                        {it.horizon_years} · {it.regions.length} regions
                                    </div>
                                </div>
                                <div className="lg:col-span-7 space-y-3 text-[14px] leading-relaxed text-neutral-800">
                                    <p>{it.detail}</p>
                                    <p className="text-neutral-600 italic border-l-2 border-[#2F6B47] pl-4">
                                        {it.investor_takeaway}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== VIDEOS + REPORTS ==================== */}
            <section className="border-b border-neutral-200 bg-[#F7F7F5]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="overline">Watch · Read</div>
                    <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        Investment videos & market reports.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-neutral-200">
                        <Link to="/videos?category=investment-analysis" className="group block border-r border-b border-neutral-200 p-8 bg-white hover:bg-brand-navy hover:text-white transition-colors">
                            <div className="overline group-hover:text-white/70">Investment Videos</div>
                            <div className="font-editorial mt-3 group-hover:italic" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                                Deep dives, tours and market updates.
                            </div>
                            <div className="mt-6 flex items-center gap-2 text-[13px]">
                                Open the library <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </Link>
                        <Link to="/insights" className="group block border-r border-b border-neutral-200 p-8 bg-white hover:bg-brand-navy hover:text-white transition-colors">
                            <div className="overline group-hover:text-white/70">Market Reports</div>
                            <div className="font-editorial mt-3 group-hover:italic" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                                Editorial reports, quarterly reads.
                            </div>
                            <div className="mt-6 flex items-center gap-2 text-[13px]">
                                Read the insights <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ==================== BOTTOM CTA ==================== */}
            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-white/60 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-white/60" />
                            When you&apos;re ready
                        </div>
                        <h2 className="font-editorial mt-6 leading-[1.03]" style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}>
                            <TrendingUp className="inline w-8 h-8 mr-3" />
                            Sixty minutes on your portfolio.
                        </h2>
                        <p className="mt-6 font-editorial text-white/80 max-w-xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                            An honest conversation about where you are, where you want to be in ten
                            years, and which of the three-to-five likeliest paths actually gets you
                            there. No listings pitched.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <Link
                            to="/investors/strategy-session"
                            className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 text-[14px] font-medium hover:bg-neutral-200"
                            style={{ letterSpacing: '0.02em' }}
                            data-testid="investor-cta-session-bottom"
                        >
                            Book a strategy session <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
